import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  AlertCircle, CheckCircle2, ChevronRight, ExternalLink,
  FileDown, Loader2, Music, Plus, RotateCcw, Upload, X, Youtube,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "steelmora_bulk_import_batch";
const POLL_MS = 3000;
const ALLOWED_EXT = [".mp3", ".wav", ".flac", ".m4a", ".ogg", ".aac"];
const CONFIDENCE_THRESHOLD = 0.85;

// ── helpers ──────────────────────────────────────────────────────────────────

function parseYouTubeId(input) {
  if (!input) return null;
  const t = input.trim();
  const watchM = t.match(/[?&]v=([A-Za-z0-9_-]{6,20})/);
  if (watchM) return watchM[1];
  const shortM = t.match(/youtu\.be\/([A-Za-z0-9_-]{6,20})/);
  if (shortM) return shortM[1];
  const shortsM = t.match(/shorts\/([A-Za-z0-9_-]{6,20})/);
  if (shortsM) return shortsM[1];
  if (/^[A-Za-z0-9_-]{6,20}$/.test(t)) return t;
  return null;
}

function stemName(filename) {
  return filename.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim();
}

function fmtConf(v) {
  if (v == null) return "—";
  return (
    <span className={v >= CONFIDENCE_THRESHOLD ? "text-tune" : v >= 0.6 ? "text-brass" : "text-muted-foreground"}>
      {(v * 100).toFixed(0)}%
    </span>
  );
}

const STATUS_META = {
  pending:      { label: "En cola",        cls: "bg-slate-500/20 text-slate-300",    icon: null },
  analyzing:    { label: "Analizando",     cls: "bg-signal/15 text-signal",      icon: <Loader2 className="h-3.5 w-3.5 animate-spin" /> },
  transcribing: { label: "Transcribiendo", cls: "bg-indigo-400/15 text-indigo-200",  icon: <Loader2 className="h-3.5 w-3.5 animate-spin" /> },
  analyzed:     { label: "Analizada",      cls: "bg-brass/15 text-brass",  icon: null },
  published:    { label: "Publicada",      cls: "bg-tune/15 text-tune", icon: <CheckCircle2 className="h-3.5 w-3.5" /> },
  failed:       { label: "Error",          cls: "bg-red-400/15 text-red-300",        icon: <AlertCircle className="h-3.5 w-3.5" /> },
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.cls}`}>
      {m.icon}{m.label}
    </span>
  );
};

// ── main component ────────────────────────────────────────────────────────────

export default function AdminBulkImportPage() {
  const { getAuthHeaders } = useAuth();

  // "setup" → user picks files + fills metadata
  // "uploading" → sequentially posting files to the API
  // "polling" → all files queued, watching analysis progress
  // "done" → no more "analyzing" items
  const [phase, setPhase] = useState("setup");

  // setup state
  const [fileEntries, setFileEntries] = useState([]); // [{id, file, title, artist, ytUrl}]
  const dragRef = useRef(null);
  const fileInputRef = useRef(null);

  // processing state
  const [batchId, setBatchId] = useState(null);
  const itemFileMapRef = useRef({}); // itemId → File (persists across renders)
  const [jobData, setJobData] = useState(null); // {job, items}
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const pollingRef = useRef(null);

  // inline YT video state (for "analyzed" items without video)
  const [inlineVideos, setInlineVideos] = useState({});
  const [savingVideo, setSavingVideo] = useState({});

  // chord complexity for this batch
  const [jobComplexity, setJobComplexity] = useState("standard");

  // publish state
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState({});

  // recovered session banner
  const [recovering, setRecovering] = useState(false);
  const [savedBatchId, setSavedBatchId] = useState(null);

  // ── on mount: check for saved session ──────────────────────────────────────
  useEffect(() => {
    const saved = localStorage.getItem(LS_KEY);
    if (saved) setSavedBatchId(saved);
  }, []);

  // ── polling ────────────────────────────────────────────────────────────────
  const poll = useCallback(async (bid) => {
    try {
      const res = await axios.get(`${API}/admin/bulk-import/jobs/${bid}`, {
        headers: getAuthHeaders(),
      });
      const data = res.data;
      setJobData(data);
      const items = data.items || [];
      const stillAnalyzing = items.some((i) => i.status === "analyzing" || i.status === "transcribing");
      if (!stillAnalyzing) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        setPhase("done");
      }
    } catch {
      // keep polling; transient error
    }
  }, [getAuthHeaders]);

  const startPolling = useCallback((bid) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    poll(bid);
    pollingRef.current = setInterval(() => poll(bid), POLL_MS);
  }, [poll]);

  useEffect(() => () => { if (pollingRef.current) clearInterval(pollingRef.current); }, []);

  // ── recover session ────────────────────────────────────────────────────────
  const recoverSession = async () => {
    if (!savedBatchId) return;
    setRecovering(true);
    try {
      const res = await axios.get(`${API}/admin/bulk-import/jobs/${savedBatchId}`, {
        headers: getAuthHeaders(),
      });
      setBatchId(savedBatchId);
      setJobData(res.data);
      const stillAnalyzing = (res.data.items || []).some((i) => i.status === "analyzing" || i.status === "transcribing");
      setPhase(stillAnalyzing ? "polling" : "done");
      if (stillAnalyzing) startPolling(savedBatchId);
      setSavedBatchId(null);
    } catch {
      toast.error("No se pudo recuperar la sesión anterior.");
      localStorage.removeItem(LS_KEY);
      setSavedBatchId(null);
    } finally {
      setRecovering(false);
    }
  };

  // ── drag & drop ────────────────────────────────────────────────────────────
  const addFiles = useCallback((rawFiles) => {
    const valid = Array.from(rawFiles).filter((f) => {
      const ext = "." + f.name.split(".").pop().toLowerCase();
      return ALLOWED_EXT.includes(ext);
    });
    if (!valid.length) {
      toast.error("Solo se aceptan archivos MP3, WAV, FLAC, M4A, OGG o AAC.");
      return;
    }
    setFileEntries((prev) => {
      const existing = new Set(prev.map((e) => e.file.name));
      const newEntries = valid
        .filter((f) => !existing.has(f.name))
        .map((f) => ({
          id: Math.random().toString(36).slice(2),
          file: f,
          title: stemName(f.name),
          artist: "",
          ytUrl: "",
        }));
      const combined = [...prev, ...newEntries].slice(0, 50);
      if (combined.length === 50) toast.info("Máximo 50 canciones por lote.");
      return combined;
    });
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    dragRef.current?.classList.remove("border-signal");
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const onDragOver = (e) => { e.preventDefault(); dragRef.current?.classList.add("border-signal"); };
  const onDragLeave = () => dragRef.current?.classList.remove("border-signal");

  const updateEntry = (id, field, value) =>
    setFileEntries((prev) => prev.map((e) => e.id === id ? { ...e, [field]: value } : e));

  const removeEntry = (id) =>
    setFileEntries((prev) => prev.filter((e) => e.id !== id));

  // ── start analysis ─────────────────────────────────────────────────────────
  const startAnalysis = async () => {
    const valid = fileEntries.filter((e) => e.title.trim() && e.artist.trim());
    if (!valid.length) {
      toast.error("Completa al menos título y artista para cada canción.");
      return;
    }

    try {
      setPhase("uploading");

      // 1. Create batch with metadata
      const itemSpecs = valid.map((e) => ({
        filename: e.file.name,
        title: e.title.trim(),
        artist: e.artist.trim(),
        youtube_video_id: parseYouTubeId(e.ytUrl) || undefined,
        youtube_url: e.ytUrl.trim() || undefined,
      }));

      const batchRes = await axios.post(
        `${API}/admin/bulk-import/jobs`,
        { items: itemSpecs, chord_complexity: jobComplexity },
        { headers: getAuthHeaders() }
      );
      const newBatchId = batchRes.data.batch_id;
      const createdItems = batchRes.data.items; // [{item_id, filename}]

      // 2. Map item_id → File (by position)
      const map = {};
      createdItems.forEach(({ item_id }, idx) => {
        map[item_id] = valid[idx].file;
      });
      itemFileMapRef.current = map;

      setBatchId(newBatchId);
      localStorage.setItem(LS_KEY, newBatchId);

      // 3. Upload files sequentially
      setUploadProgress({ done: 0, total: createdItems.length });
      for (let i = 0; i < createdItems.length; i++) {
        const { item_id, filename } = createdItems[i];
        const file = map[item_id];
        if (!file) continue;

        const fd = new FormData();
        fd.append("file", file, filename);

        try {
          await axios.post(
            `${API}/admin/bulk-import/jobs/${newBatchId}/items/${item_id}/upload`,
            fd,
            { headers: getAuthHeaders() }
          );
        } catch (err) {
          toast.error(`Error al subir "${filename}": ${err.response?.data?.detail || err.message}`);
        }
        setUploadProgress({ done: i + 1, total: createdItems.length });
      }

      // 4. All uploaded → start polling
      setPhase("polling");
      startPolling(newBatchId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo crear el lote.");
      setPhase("setup");
    }
  };

  // ── add video inline ───────────────────────────────────────────────────────
  const addVideo = async (itemId) => {
    const url = inlineVideos[itemId] || "";
    const ytId = parseYouTubeId(url);
    if (!ytId) {
      toast.error("URL de YouTube inválida.");
      return;
    }
    setSavingVideo((p) => ({ ...p, [itemId]: true }));
    try {
      await axios.patch(
        `${API}/admin/bulk-import/items/${itemId}/video`,
        { youtube_video_id: ytId },
        { headers: getAuthHeaders() }
      );
      toast.success("Video asignado.");
      if (batchId) poll(batchId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo asignar el video.");
    } finally {
      setSavingVideo((p) => ({ ...p, [itemId]: false }));
    }
  };

  // ── batch publish ──────────────────────────────────────────────────────────
  const publishReady = async () => {
    if (!batchId) return;
    setPublishing(true);
    try {
      const res = await axios.post(
        `${API}/admin/bulk-import/jobs/${batchId}/publish`,
        {},
        { headers: getAuthHeaders() }
      );
      setPublishResult(res.data);
      toast.success(`${res.data.published_count} canción(es) publicadas.`);
      poll(batchId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al publicar.");
    } finally {
      setPublishing(false);
    }
  };

  // ── PDF download ──────────────────────────────────────────────────────────
  const downloadPdf = async (songId, title) => {
    setDownloadingPdf((p) => ({ ...p, [songId]: true }));
    try {
      const res = await axios.get(
        `${API}/admin/songs/${songId}/export/pdf`,
        { headers: getAuthHeaders(), responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${title || "partitura"}_admin.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar el PDF.");
    } finally {
      setDownloadingPdf((p) => ({ ...p, [songId]: false }));
    }
  };

  // ── new session ────────────────────────────────────────────────────────────
  const newSession = () => {
    clearInterval(pollingRef.current);
    pollingRef.current = null;
    localStorage.removeItem(LS_KEY);
    setBatchId(null);
    setJobData(null);
    setFileEntries([]);
    setInlineVideos({});
    setPublishResult(null);
    itemFileMapRef.current = {};
    setPhase("setup");
  };

  // ── derived values ─────────────────────────────────────────────────────────
  const items = jobData?.items || [];
  const job = jobData?.job || {};
  const analyzedWithVideo = items.filter(
    (i) => i.status === "analyzed" && i.youtube_video_id
  );
  const analyzedWithoutVideo = items.filter(
    (i) => i.status === "analyzed" && !i.youtube_video_id
  );
  const isProcessing = phase === "uploading" || phase === "polling";

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <section className="steel-page space-y-5">
      {/* Header */}
      <header className="flex flex-col gap-4 rounded-3xl border border-border bg-white/[0.045] p-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-signal/65">Administration</p>
          <h1 className="mt-1 text-3xl font-bold text-foreground">Bulk Import</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Sube hasta 50 canciones, analiza con IA y publica en el catálogo de YouTube Practice.
          </p>
        </div>
        <nav className="flex gap-2">
          <Link to="/admin/music-catalog" className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-border px-4 text-sm text-slate-300 hover:text-foreground">
            <ChevronRight className="h-4 w-4 rotate-180" /> Catálogo
          </Link>
          {(phase === "done" || phase === "polling") && (
            <button onClick={newSession} className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-border px-4 text-sm text-slate-300 hover:text-foreground">
              <RotateCcw className="h-4 w-4" /> Nueva sesión
            </button>
          )}
        </nav>
      </header>

      {/* Recover session banner */}
      {savedBatchId && phase === "setup" && (
        <div className="flex flex-col gap-3 rounded-2xl border border-brass/20 bg-brass/[0.06] p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <RotateCcw className="h-5 w-5 shrink-0 text-brass" />
            <p className="text-sm text-brass">
              Tienes una sesión guardada. ¿Quieres continuar donde lo dejaste?
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { localStorage.removeItem(LS_KEY); setSavedBatchId(null); }}
              className="rounded-lg px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
            >
              Descartar
            </button>
            <button
              onClick={recoverSession}
              disabled={recovering}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brass/20 px-3 py-1.5 text-xs font-semibold text-brass hover:bg-brass/30 disabled:opacity-50"
            >
              {recovering && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Reanudar sesión
            </button>
          </div>
        </div>
      )}

      {/* ── SETUP PHASE ──────────────────────────────────────────────────── */}
      {phase === "setup" && (
        <>
          {/* Drop zone */}
          <div
            ref={dragRef}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            className="flex min-h-44 cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-border bg-panel/50 transition-colors hover:border-signal/60"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-10 w-10 text-muted-foreground" />
            <p className="text-base font-semibold text-slate-300">
              Arrastra archivos aquí o haz clic para seleccionar
            </p>
            <p className="text-xs text-muted-foreground">MP3, WAV, FLAC, M4A, OGG, AAC · máx. 50 archivos · 200 MB c/u</p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ALLOWED_EXT.join(",")}
              className="hidden"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
            />
          </div>

          {/* Metadata table */}
          {fileEntries.length > 0 && (
            <div className="rounded-2xl border border-border bg-white/[0.03] overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-3 w-8">#</th>
                    <th className="px-4 py-3">Archivo</th>
                    <th className="px-4 py-3 min-w-[160px]">Título *</th>
                    <th className="px-4 py-3 min-w-[140px]">Artista *</th>
                    <th className="px-4 py-3 min-w-[220px]">YouTube URL (opcional)</th>
                    <th className="px-4 py-3 w-8" />
                  </tr>
                </thead>
                <tbody>
                  {fileEntries.map((entry, idx) => (
                    <tr key={entry.id} className="border-b border-white/[0.06] last:border-0">
                      <td className="px-4 py-2.5 text-muted-foreground">{idx + 1}</td>
                      <td className="px-4 py-2.5">
                        <span className="flex items-center gap-2 text-slate-300">
                          <Music className="h-3.5 w-3.5 shrink-0 text-signal" />
                          <span className="max-w-[160px] truncate">{entry.file.name}</span>
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <input
                          value={entry.title}
                          onChange={(e) => updateEntry(entry.id, "title", e.target.value)}
                          placeholder="Título de la canción"
                          className="w-full rounded-lg border border-border bg-ink/40 px-2.5 py-1.5 text-sm text-foreground outline-none focus:border-signal/50"
                        />
                      </td>
                      <td className="px-4 py-2.5">
                        <input
                          value={entry.artist}
                          onChange={(e) => updateEntry(entry.id, "artist", e.target.value)}
                          placeholder="Artista"
                          className="w-full rounded-lg border border-border bg-ink/40 px-2.5 py-1.5 text-sm text-foreground outline-none focus:border-signal/50"
                        />
                      </td>
                      <td className="px-4 py-2.5">
                        <input
                          value={entry.ytUrl}
                          onChange={(e) => updateEntry(entry.id, "ytUrl", e.target.value)}
                          placeholder="https://youtube.com/watch?v=..."
                          className="w-full rounded-lg border border-border bg-ink/40 px-2.5 py-1.5 text-sm text-foreground outline-none focus:border-signal/50"
                        />
                      </td>
                      <td className="px-4 py-2.5">
                        <button
                          onClick={() => removeEntry(entry.id)}
                          className="rounded-lg p-1 text-muted-foreground hover:text-red-400"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Start button + complexity selector */}
          {fileEntries.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-sm text-muted-foreground">
                  {fileEntries.length} archivo(s) · Completa título y artista antes de iniciar
                </p>
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  Complejidad de acordes
                  <select
                    className="rounded-lg border border-border bg-black/30 px-2 py-1.5 text-xs text-foreground"
                    value={jobComplexity}
                    onChange={(e) => setJobComplexity(e.target.value)}
                  >
                    <option value="basic">Básico (mayor/menor)</option>
                    <option value="standard">Estándar (+ 7, dim)</option>
                    <option value="extended">Extendido (+ maj7, m7, aug)</option>
                    <option value="full">Completo (sin filtro)</option>
                  </select>
                </label>
              </div>
              <button
                onClick={startAnalysis}
                className="inline-flex items-center gap-2 rounded-xl bg-signal px-6 py-3 text-sm font-semibold text-ink hover:bg-signal disabled:opacity-50"
              >
                <Upload className="h-4 w-4" />
                Iniciar análisis ({fileEntries.filter((e) => e.title.trim() && e.artist.trim()).length} canciones)
              </button>
            </div>
          )}
        </>
      )}

      {/* ── UPLOADING PHASE ──────────────────────────────────────────────── */}
      {phase === "uploading" && (
        <div className="rounded-2xl border border-border bg-white/[0.03] p-6 text-center space-y-4">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-signal" />
          <p className="text-base font-semibold text-foreground">
            Subiendo archivos al servidor…
          </p>
          <p className="text-sm text-muted-foreground">
            {uploadProgress.done} / {uploadProgress.total} archivos enviados
          </p>
          <div className="mx-auto h-2 max-w-xs rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full rounded-full bg-signal transition-all"
              style={{ width: `${uploadProgress.total ? (uploadProgress.done / uploadProgress.total) * 100 : 0}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground">El análisis ocurre en background — puedes cerrar el tab y volver.</p>
        </div>
      )}

      {/* ── POLLING / DONE PHASE ─────────────────────────────────────────── */}
      {(phase === "polling" || phase === "done") && jobData && (
        <>
          {/* Batch summary */}
          <div className="grid gap-3 sm:grid-cols-4">
            {[
              { label: "Total", value: job.total_items || 0, cls: "text-foreground" },
              { label: "Completadas", value: job.completed_items || 0, cls: "text-brass" },
              { label: "Publicadas", value: job.published_items || 0, cls: "text-tune" },
              { label: "Con error", value: job.failed_items || 0, cls: "text-red-300" },
            ].map(({ label, value, cls }) => (
              <article key={label} className="rounded-2xl border border-border bg-white/[0.045] p-4">
                <p className={`text-3xl font-bold ${cls}`}>{value}</p>
                <p className="text-sm text-muted-foreground">{label}</p>
              </article>
            ))}
          </div>

          {/* Overall progress bar (while polling) */}
          {phase === "polling" && (
            <div className="flex items-center gap-3">
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-signal" />
              <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full rounded-full bg-signal transition-all duration-500"
                  style={{
                    width: `${job.total_items
                      ? ((job.completed_items + job.failed_items) / job.total_items) * 100
                      : 0}%`,
                  }}
                />
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {job.completed_items + (job.failed_items || 0)} / {job.total_items}
              </span>
            </div>
          )}

          {/* Items table */}
          <div className="rounded-2xl border border-border bg-white/[0.03] overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">Canción</th>
                  <th className="px-4 py-3">Key</th>
                  <th className="px-4 py-3">BPM</th>
                  <th className="px-4 py-3">Conf.</th>
                  <th className="px-4 py-3">Acordes</th>
                  <th className="px-4 py-3">Video</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3 w-8" />
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    inlineUrl={inlineVideos[item.id] || ""}
                    savingVideo={!!savingVideo[item.id]}
                    downloadingPdf={!!downloadingPdf[item.song_id]}
                    onUrlChange={(v) => setInlineVideos((p) => ({ ...p, [item.id]: v }))}
                    onAddVideo={() => addVideo(item.id)}
                    onPdfDownload={() => downloadPdf(item.song_id, item.title)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Publish result */}
          {publishResult && (
            <div className="rounded-2xl border border-tune/20 bg-tune/[0.06] p-4">
              <p className="text-sm font-semibold text-tune">
                ✅ {publishResult.published_count} canción(es) publicadas en YouTube Practice.
              </p>
              {publishResult.skipped?.length > 0 && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {publishResult.skipped.length} omitida(s): sin video asignado.
                </p>
              )}
            </div>
          )}

          {/* Action bar */}
          {phase === "done" && (
            <div className="flex flex-wrap gap-3">
              {analyzedWithVideo.length > 0 && (
                <button
                  onClick={publishReady}
                  disabled={publishing}
                  className="inline-flex items-center gap-2 rounded-xl bg-tune px-6 py-3 text-sm font-semibold text-foreground hover:bg-tune disabled:opacity-50"
                >
                  {publishing
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <CheckCircle2 className="h-4 w-4" />}
                  Publicar listas ({analyzedWithVideo.length})
                </button>
              )}
              {analyzedWithoutVideo.length > 0 && (
                <p className="self-center text-xs text-brass">
                  {analyzedWithoutVideo.length} canción(es) analizadas sin video — añade la URL debajo para publicarlas.
                </p>
              )}
              <Link
                to="/admin/music-catalog"
                className="inline-flex items-center gap-2 rounded-xl border border-border px-5 py-3 text-sm text-slate-300 hover:text-foreground"
              >
                <ExternalLink className="h-4 w-4" /> Ver catálogo
              </Link>
            </div>
          )}
        </>
      )}
    </section>
  );
}

// ── ItemRow sub-component ─────────────────────────────────────────────────────

function ItemRow({ item, inlineUrl, savingVideo, downloadingPdf, onUrlChange, onAddVideo, onPdfDownload }) {
  const needsVideo = item.status === "analyzed" && !item.youtube_video_id;

  return (
    <>
      <tr className="border-b border-white/[0.06] last:border-0">
        <td className="px-4 py-2.5">
          <div>
            <p className="font-medium text-foreground">{item.title}</p>
            <p className="text-xs text-muted-foreground">{item.artist} · {item.filename}</p>
          </div>
        </td>
        <td className="px-4 py-2.5 text-slate-300">{item.key || "—"}</td>
        <td className="px-4 py-2.5 text-slate-300">{item.bpm || "—"}</td>
        <td className="px-4 py-2.5">{fmtConf(item.confidence)}</td>
        <td className="px-4 py-2.5">
          {item.unique_chords?.length ? (
            <span className="flex flex-wrap gap-1" title={`${item.chords_count ?? "?"} eventos`}>
              {item.unique_chords.slice(0, 6).map((c) => (
                <span key={c} className="rounded-md bg-signal/10 px-1.5 py-0.5 text-[11px] font-semibold text-signal">{c}</span>
              ))}
              {item.unique_chords.length > 6 && (
                <span className="text-[11px] text-muted-foreground">+{item.unique_chords.length - 6}</span>
              )}
            </span>
          ) : (
            <span className="text-muted-foreground">{item.chords_count ?? "—"}</span>
          )}
        </td>
        <td className="px-4 py-2.5">
          {item.youtube_video_id ? (
            <a
              href={`https://www.youtube.com/watch?v=${item.youtube_video_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-red-400 hover:text-red-300"
            >
              <Youtube className="h-3.5 w-3.5" />
              {item.youtube_video_id}
            </a>
          ) : (
            <span className="text-xs text-muted-foreground">Sin video</span>
          )}
        </td>
        <td className="px-4 py-2.5">
          <StatusBadge status={item.status} />
        </td>
        <td className="px-4 py-2.5">
          <div className="flex items-center gap-1">
            {item.song_id && (
              <Link
                to={`/admin/music-catalog/${item.song_id}/edit`}
                className="rounded-lg p-1 text-muted-foreground hover:text-signal"
                title="Editar en catálogo"
              >
                <ExternalLink className="h-4 w-4" />
              </Link>
            )}
            {item.song_id && (item.status === "analyzed" || item.status === "published") && (
              <button
                type="button"
                onClick={onPdfDownload}
                disabled={downloadingPdf}
                className="rounded-lg p-1 text-muted-foreground hover:text-brass disabled:opacity-40"
                title="Descargar PDF admin"
              >
                {downloadingPdf
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <FileDown className="h-4 w-4" />}
              </button>
            )}
          </div>
        </td>
      </tr>

      {/* Error row */}
      {item.status === "failed" && item.error && (
        <tr className="border-b border-white/[0.06]">
          <td colSpan={8} className="px-4 pb-3 pt-0">
            <p className="rounded-lg bg-red-400/[0.08] px-3 py-2 text-xs text-red-300">
              {item.error}
            </p>
          </td>
        </tr>
      )}

      {/* Inline YouTube URL input for analyzed items without video */}
      {needsVideo && (
        <tr className="border-b border-white/[0.06]">
          <td colSpan={8} className="px-4 pb-3 pt-0">
            <div className="flex items-center gap-2">
              <Youtube className="h-4 w-4 shrink-0 text-red-400" />
              <input
                value={inlineUrl}
                onChange={(e) => onUrlChange(e.target.value)}
                placeholder="Pega la URL de YouTube para poder publicar"
                className="flex-1 rounded-lg border border-border bg-ink/40 px-2.5 py-1.5 text-sm text-foreground outline-none focus:border-signal/50"
                onKeyDown={(e) => e.key === "Enter" && onAddVideo()}
              />
              <button
                onClick={onAddVideo}
                disabled={savingVideo || !inlineUrl.trim()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-signal/20 px-3 py-1.5 text-xs font-semibold text-signal hover:bg-signal/30 disabled:opacity-40"
              >
                {savingVideo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                Añadir
              </button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
